import { Alert, Avatar, Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { UsersThreeIcon, XIcon } from '@phosphor-icons/react';

import { useOrgSecurityGroups } from '@/hooks/use-page-editors';
import { useSiteAdmins } from '@/hooks/use-site-admins';
import { useSitePageEditors } from '@/hooks/use-site-page-editors';
import { useUnitMemberDirectory } from '@/hooks/use-unit-member-directory';
import type { Page } from '@/types/database';

interface PeopleDrawerProps {
  open: boolean;
  onClose: () => void;
  pages: Page[];
}

// The "Permissions Review" destination (spec section 5, labeled "People"
// there) — who can touch this site. Two tiers: org-wide admins (from Clerk's own
// role, always able to manage everything) and page-scoped editors (the
// "den leader" delegated access in page_editors, one row per page — either
// a specific person or a security group, whose current membership is
// managed in Scouting Ops Hub). Fine-grained non-admin org-wide grants
// (permission templates) are also managed in Hub, not duplicated here —
// see use-site-admins.ts.
export function PeopleDrawer({ open, onClose, pages }: PeopleDrawerProps) {
  const { admins, isLoading: adminsLoading } = useSiteAdmins();
  const { data: pageEditors, isLoading: editorsLoading } = useSitePageEditors(pages);
  const { directory } = useUnitMemberDirectory();
  const { groups } = useOrgSecurityGroups();
  const pageById = new Map(pages.map((page) => [page.id, page]));
  const groupById = new Map(groups.map((group) => [group.id, group]));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 420 } } } }}>
      <Stack direction="row" sx={{ alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          Permissions Review
        </Typography>
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </Stack>

      <Stack spacing={3} sx={{ p: 2.5 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            Admins — can manage the whole site
          </Typography>
          {adminsLoading ? null : admins.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No org admins found.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {admins.map((admin) => (
                <Stack key={admin.userId} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                  <Avatar src={admin.imageUrl} sx={{ width: 28, height: 28 }}>
                    {admin.name[0]}
                  </Avatar>
                  <Typography variant="body2">{admin.name}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Manage who has the org Admin role, or grant finer-grained website access without full admin, in Scouting Ops Hub.
          </Typography>
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
            Page editors — can manage one page only
          </Typography>
          {editorsLoading ? null : !pageEditors || pageEditors.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No page-specific editors assigned. Assign one from a page&apos;s Settings → Access tab.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {pageEditors.map((editor) => {
                const isGroup = Boolean(editor.security_group_id);
                const member = editor.clerk_user_id ? directory.get(editor.clerk_user_id) : undefined;
                const group = editor.security_group_id ? groupById.get(editor.security_group_id) : undefined;
                const page = pageById.get(editor.page_id);
                return (
                  <Stack key={editor.id} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    {isGroup ? (
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
                        <UsersThreeIcon size={14} />
                      </Avatar>
                    ) : (
                      <Avatar src={member?.imageUrl} sx={{ width: 28, height: 28 }}>
                        {member?.name?.[0] ?? '?'}
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">
                        {isGroup ? (group?.name ?? 'Security group') : (member?.name ?? editor.clerk_user_id)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {page?.title ?? 'Unknown page'}
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>

        {admins.length < 2 ? (
          <Alert severity="warning">
            Only one admin can manage this site. If they lose access, no one else can update it. Add a second admin in Scouting Ops
            Hub.
          </Alert>
        ) : null}
      </Stack>
    </Drawer>
  );
}
